import { t } from "@lingui/macro";
import _ from "lodash";
import React from "react";

import {
  InfoBannerCautionMinorIcon,
  InfoBannerFailureIcon,
} from "@whoosh/design-system/src/components/display/InfoBanner";
import {
  Box,
  Text,
  HStack,
} from "@whoosh/design-system/src/components/primitive";
import { WhooshThemeColors } from "@whoosh/design-system/src/theme/restyle/theme";
import {
  CustomColor,
  getEventColorKeyFromServerColor,
} from "@whoosh/utilities/src/colors";

import { DepartmentIcon } from "../../../../components/icons_v2/department/components/DepartmentIcon";
import {
  ClockIcon,
  CalendarFilledIcon,
  PlaceIcon,
} from "../../../../components/icons_v2/general";
import { Pressable } from "../../../../components/primitive/Pressable/Pressable";
import { Avatar } from "./Avatar";
import { SignupStatus, SignupStatusOption } from "./SignupStatus";

type DepartmentType =
  | "AQUATICS"
  | "BASKETBALL"
  | "BOATING"
  | "BUSINESS"
  | "CARD_ROOM"
  | "CLUB"
  | "CROQUET"
  | "DINING"
  | "FITNESS"
  | "GOLF"
  | "RACQUET"
  | "SALON"
  | "SPA"
  | "AXE_THROWING"
  | "BOCCE_BALL"
  | "HOCKEY"
  | "SHUFFLEBOARD";

export type MyScheduleCardProps = {
  title: string;
  subTitle: string;
  date: string;
  timeRange?: string | null;
  cardColor: CustomColor;
  departmentType: DepartmentType;
  departmentName: string;
  courseNames?: string;
  onPress?: () => void;
  signupStatus?: SignupStatusOption | null;
  playerAvatarData?: {
    profileImageId?: string;
    firstName: string | null;
    lastName: string | null;
  }[];
  hasPairings?: boolean;
  hasAvailableTimes?: boolean;
  requestRestrictionStatus?: "FULL" | "PARTIAL";
  testID?: string;
};

type RequestRestrictionStatusProps = {
  status: "FULL" | "PARTIAL";
};

const RequestRestrictionStatus = ({
  status,
}: RequestRestrictionStatusProps) => {
  const isFull = status === "FULL";
  const StatusIcon = isFull
    ? InfoBannerFailureIcon
    : InfoBannerCautionMinorIcon;

  return (
    <Box
      testID={`request-card-conflict-${status.toLowerCase()}`}
      backgroundColor={
        isFull
          ? "surface-status-error-subtlest"
          : "surface-status-warning-subtlest"
      }
      borderRadius="1"
      p={1}
    >
      <StatusIcon
        width={16}
        height={16}
        color={isFull ? "icon-on-color-error" : "icon-on-color-warning"}
      />
    </Box>
  );
};

export const MyScheduleCard = ({
  onPress,
  title,
  subTitle,
  date,
  timeRange,
  courseNames,
  cardColor,
  departmentType,
  departmentName,
  signupStatus,
  playerAvatarData,
  hasPairings,
  hasAvailableTimes,
  requestRestrictionStatus,
  testID,
}: MyScheduleCardProps) => {
  const eventColorPrimitiveString = getEventColorKeyFromServerColor(
    cardColor,
    "surface"
  );

  let mainColor: WhooshThemeColors = "surface-main-primary-subtle";
  if (signupStatus === "CONFIRMED" || signupStatus === "WAITLIST") {
    mainColor = `${eventColorPrimitiveString}-subtle` as WhooshThemeColors;
  } else if (signupStatus === "REQUEST") {
    mainColor = "surface-secondary";
  }

  return (
    <Pressable
      onPress={onPress}
      borderRadius="4"
      backgroundColor={mainColor}
      flexShrink={0}
      flexGrow={1}
      testID={testID}
    >
      <HStack
        justifyContent="space-between"
        alignItems="center"
        height={44}
        px={5}
        mt={1}
      >
        <HStack>
          <DepartmentIcon type={departmentType} />
          <Text variant="mainSmallSemiBold" ml={2}>
            {departmentName || t`Golf`}
          </Text>
        </HStack>

        {playerAvatarData ? (
          <HStack>
            {playerAvatarData.length <= 4
              ? playerAvatarData.map((player, index) => {
                  const isLast = index === playerAvatarData.length - 1;
                  return (
                    <Avatar
                      key={`${player.firstName}-${index}`}
                      player={player}
                      mainColor={mainColor}
                      isLast={isLast}
                    />
                  );
                })
              : playerAvatarData
                  .filter((_, i) => i < 3)
                  .map((player, index) => {
                    const isLast = index === playerAvatarData.length - 1;
                    return (
                      <Avatar
                        key={`player.name-${index}`}
                        player={player}
                        mainColor={mainColor}
                        isLast={isLast}
                      />
                    );
                  })}
            {playerAvatarData.length > 4 ? (
              <Box
                width={27}
                height={27}
                justifyContent="center"
                alignItems="center"
                bg="surface-primary"
                borderRadius="1"
                borderWidth={1.5}
                borderColor={mainColor}
              >
                <Text variant="mainSmallSemiBold">{`+${
                  playerAvatarData.length - 3
                }`}</Text>
              </Box>
            ) : null}
          </HStack>
        ) : null}
        {signupStatus ? <SignupStatus status={signupStatus} /> : null}
      </HStack>
      <Box
        flexDirection={{ base: "column-reverse", md: "row" }}
        p={6}
        bg="grayscale-0"
        my={1}
        mx={1}
        borderRadius="2"
        flex={1}
      >
        <Box flex={1} alignItems="flex-start" maxWidth="100%">
          {/** Top */}
          <Box
            maxWidth="100%"
            width={
              hasAvailableTimes || requestRestrictionStatus ? "100%" : "auto"
            }
          >
            {title || requestRestrictionStatus ? (
              <HStack
                gap={1}
                width="100%"
                alignItems="center"
                justifyContent="space-between"
              >
                <HStack gap={1} flexShrink={1} alignItems="center">
                  {title ? (
                    <Text variant="mainSmallSemiBold" color="text-secondary">
                      {_.capitalize((title || "").toLowerCase())}
                    </Text>
                  ) : null}
                  {hasPairings ? (
                    <Text
                      color={`text-user-selectable-color${cardColor ?? "C"}`}
                      variant="mainSmallSemiBold"
                    >{t`Pairings Assigned`}</Text>
                  ) : null}
                </HStack>
                {requestRestrictionStatus ? (
                  <RequestRestrictionStatus status={requestRestrictionStatus} />
                ) : null}
              </HStack>
            ) : null}
            {subTitle ? (
              hasAvailableTimes ? (
                <HStack
                  mt={2}
                  alignItems="center"
                  justifyContent={"space-between"}
                >
                  <Text variant="serifHeading" numberOfLines={2}>
                    {subTitle || ""}
                  </Text>
                  <Box
                    backgroundColor="main-primary-300"
                    paddingLeft={2}
                    paddingRight={2}
                    paddingTop={1}
                    paddingBottom={1}
                    borderRadius="4"
                  >
                    <Text
                      variant="mainTiny"
                      color="text-primary"
                    >{t`Times Available!`}</Text>
                  </Box>
                </HStack>
              ) : (
                <Box mt={2}>
                  <Text variant="serifHeading" numberOfLines={2}>
                    {subTitle || ""}
                  </Text>
                </Box>
              )
            ) : null}
          </Box>

          {/* Mid */}
          <Box mt={4}>
            {date ? (
              <HStack>
                <Box
                  width={16}
                  height={16}
                  justifyContent="center"
                  alignItems="center"
                >
                  <CalendarFilledIcon />
                </Box>
                <Text variant="mainBaseSemiBold" ml={2}>
                  {date}
                </Text>
              </HStack>
            ) : null}

            {timeRange ? (
              <HStack mt="1.5">
                <Box
                  width={16}
                  height={16}
                  justifyContent="center"
                  alignItems="center"
                >
                  <ClockIcon />
                </Box>
                <Text variant="mainBaseSemiBold" ml={2}>
                  {timeRange}
                </Text>
              </HStack>
            ) : null}

            {courseNames ? (
              <HStack mt="1.5">
                <Box
                  width={16}
                  height={16}
                  justifyContent="center"
                  alignItems="center"
                >
                  <PlaceIcon />
                </Box>
                <Text variant="mainBaseSemiBold" ml={2}>
                  {courseNames}
                </Text>
              </HStack>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Pressable>
  );
};
